import { newGuid } from '@opten/common/new-guid';
import { addClass } from '@opten/dom/add-class';
import { removeClass } from '@opten/dom/remove-class';
import { createNode } from '@opten/dom/create-node';
import { KEYCODES } from '@opten/keycodes';
import {
	overlay,
	Overlay,
	OverlayOptions,
	ConnectedPositionStrategy,
	OverlayPositionBuilder,
	OriginConnectionPosition,
	OverlayConnectionPosition,
} from '../overlay';
import { parseCssUnit } from '@opten/number/parse-css-unit';
import { getCss } from '@opten/dom/get-css';
import { RepositionScrollStrategy } from '../overlay/scroll/reposition-scroll-strategy';

/**
 * The following style constants are necessary to save here in order
 * to properly calculate the alignment of the selected option over
 * the trigger element.
 */

export class SelectOptions {
	classes: {
		component: string;
		active: string;
		highlighted: string;
		disabled: string;
		iconHtml?: string;
	};
}

export class Option {
	constructor(private element: HTMLOptionElement) {
		// this properties are cached
		this.text = (this.element.textContent || '').trim();
		this.disabled = this.element.disabled;
	}

	get disabled() {
		return this._disabled;
	}
	set disabled(value: boolean) {
		this._disabled = value;
	}
	private _disabled: boolean;

	get text() {
		return this._text;
	}
	set text(value: string) {
		this._text = value;
	}
	private _text: string;

	get value() {
		// don't cache value, might change!
		return (this.element.value || '').trim();
	}
}

export class Select {
	private overlayRef: Overlay;
	private position: ConnectedPositionStrategy;

	private select: HTMLElement;
	private options: Option[];
	private selectedIndex: number;
	private activeIndex: number;
	private uuid: string;

	private triggerElement: HTMLElement;
	private triggerRect: ClientRect;
	private optionElements: HTMLElement[];

	public name: string;
	public htmlSelectElement: HTMLSelectElement;

	get value() {
		// don't cache value, might change!
		return (this.htmlSelectElement.value || '').trim();
	}

	private config: SelectOptions = {
		classes: {
			component: 'c-select',
			active: 'is-active',
			highlighted: 'is-highlighted',
			disabled: 'is-disabled',
		},
	};

	/** The scroll position of the overlay panel, calculated to center the selected option. */
	private scrollTop = 0;

	/**
	 * The y-offset of the overlay panel in relation to the trigger's top start corner.
	 * This must be adjusted to align the selected option text over the trigger text.
	 * when the panel opens. Will change based on the y-position of the selected option.
	 */
	private offsetY = 0;

	/**
	 * This position config ensures that the top "start" corner of the overlay
	 * is aligned with with the top "start" of the origin by default (overlapping
	 * the trigger completely). If the panel cannot fit below the trigger, it
	 * will fall back to a position above the trigger.
	 */
	private positions = [
		{
			originX: 'start',
			originY: 'top',
			overlayX: 'start',
			overlayY: 'top',
		},
		{
			originX: 'start',
			originY: 'bottom',
			overlayX: 'start',
			overlayY: 'bottom',
		},
	];

	/** Whether or not the overlay panel is open. */
	private panelOpen = false;

	private panelHTML: string;

	constructor(
		private selectElement: HTMLSelectElement,
		options?: SelectOptions
	) {
		this.name = this.selectElement.name;
		this.htmlSelectElement = this.selectElement;

		this.config = { ...this.config, ...options };

		this.selectedIndex = this._getSelectedIndex();

		this.activeIndex = !this.selectedIndex ? 0 : this.selectedIndex;

		this.uuid = newGuid();

		this._init();
	}

	open(): void {
		// todo: implement disabled?
		if (
			/*this.disabled ||*/ !this.options ||
			!this.options.length ||
			this.panelOpen
		) {
			return;
		}

		this.triggerRect = this.triggerElement.getBoundingClientRect();

		this.panelOpen = true;
		this._createOverlay().open();

		this._calculateOverlayPosition();
		this._highlightCorrectOption();
		this._scrollActiveOptionIntoView();
	}

	close(): void {
		if (this.panelOpen) {
			this.overlayRef.close();

			// todo: gets called twice?
			// todo: correct? forward events
			this._shouldForward('blur');
		}
	}

	toggle(): void {
		this.panelOpen ? this.close() : this.open();
	}

	private _init() {
		// todo: set arias like Angular Material
		this.options = Array.from(this.selectElement.children).map(
			(option: HTMLOptionElement) => new Option(option)
		);

		this.selectElement.tabIndex = -1;
		this.selectElement.setAttribute('aria-hidden', 'true');
		this.selectElement.setAttribute('data-select', this.uuid);
		this.selectElement.insertAdjacentHTML('afterend', this._render());
		this.selectElement.addEventListener('focus', () => {
			this.select.focus();
			this.open();
		});

		this.select = document.getElementById(this.uuid);
		this.select.tabIndex = 0;
		this.select.className += ' ' + this.selectElement.className;

		this.triggerElement = this.select.getElementsByClassName(
			`${this.config.classes.component}__trigger`
		)[0] as HTMLElement;

		this.triggerElement.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			this.toggle();
		});

		this.select.addEventListener('keydown', (event: KeyboardEvent) =>
			this._handleKeydown(event)
		);

		this.selectElement.addEventListener('change', () =>
			this._emit(this.options[this.selectElement.selectedIndex], false)
		);
	}

	private _handleActiveIndex(event: KeyboardEvent) {
		const delta = event.keyCode === KEYCODES.UP ? -1 : 1;

		if (this.activeIndex + delta <= 0) {
			return;
		}

		if (this.activeIndex + delta >= this.options.length) {
			return;
		}

		// continue to move up/down the list until it finds an item that is not disabled
		for (let i = 1; i <= this.options.length; i++) {
			const index =
				(this.activeIndex + delta * i + this.options.length) %
				this.options.length;
			const item = this.options[index];

			if (!item.disabled) {
				this._updateActiveItem(index);
				return;
			}
		}
	}

	private _updateActiveItem(index: number) {
		this.activeIndex = index;
	}

	private _handleKeydown(event: KeyboardEvent) {
		switch (event.keyCode) {
			case KEYCODES.SPACE:
				this.toggle();
				break;
			case KEYCODES.DOWN:
			case KEYCODES.UP:
				this._handleActiveIndex(event);

				// todo: something like subscribe on selectedIndex change
				// so we can call this method there
				if (this.panelOpen) {
					this._highlightCorrectOption();
					this._scrollActiveOptionIntoView();
				} else {
					this._emit(this.options[this.activeIndex]);
				}
				break;
			case KEYCODES.ESCAPE:
				if (this.panelOpen) {
					this.close();
				}
				break;
			case KEYCODES.ENTER:
				if (!this.panelOpen) {
					this.toggle();
				} else {
					this._emit(this.options[this.activeIndex]);

					// todo: something like subscribe on activeIndex change
					// so we can call this method there
					this._highlightCorrectOption();
					this.close();
				}
				break;
			default:
				break;
		}

		event.preventDefault();
		event.stopPropagation();
	}

	/**
	 * This method creates the overlay from the provided select's template and saves its
	 * OverlayRef so that it can be attached to the DOM when open is called.
	 */
	private _createOverlay(): Overlay {
		if (!this.overlayRef) {
			this.overlayRef = overlay(
				createNode(this.panelHTML),
				this._getOverlayOptions()
			);

			this.overlayRef.backdropClick = () => this.close();

			this.overlayRef.afterOpened = () => this._onPanelDone();

			this.overlayRef.afterClosed = () => this.overlayRef.dispose();

			this.overlayRef.onAttached = () => {
				this.overlayRef.portalElement.style.transformOrigin = 'top'; // set initial origin
				this._calculateOverlayOffsetX();
				this.overlayRef.portalElement.scrollTop = this.scrollTop;
			};

			// todo: better way? we need that here because of the RepositionScrollStrategy
			this.overlayRef.onDetached = () => (this.panelOpen = false);

			this.optionElements = Array.from(
				this.overlayRef.portalElement.getElementsByClassName(
					`${this.config.classes.component}__panel__option`
				)
			) as HTMLElement[];

			this.optionElements.forEach((option, index) => {
				option.addEventListener('click', () => {
					const option = this.options[index];

					if (option.disabled) {
						return;
					}

					this._emit(option);

					// todo: something like subscribe on selectedIndex change
					// so we can call this method there
					this._highlightCorrectOption();
					this.close();
				});
			});
		}

		return this.overlayRef;
	}

	private _getOverlayOptions(): OverlayOptions {
		return new OverlayOptions({
			scrollStrategy: new RepositionScrollStrategy(),
			positionStrategy: this.position = this._createPositionStrategy(),
			backdropClassName: 'c-overlay-transparent-backdrop',
			hasBackdrop: true,
		});
	}

	private _createPositionStrategy(): ConnectedPositionStrategy {
		const primaryPosition = this.positions[0];
		const originPoint = {
			originX: primaryPosition.originX,
			originY: primaryPosition.originY,
		} as OriginConnectionPosition;
		const overlayPoint = {
			overlayX: primaryPosition.overlayX,
			overlayY: primaryPosition.overlayY,
		} as OverlayConnectionPosition;

		// todo: like angular material this.overlayRef.position()?
		const strategy = new OverlayPositionBuilder()
			.connectedTo(this.triggerElement, originPoint, overlayPoint)
			.withOffsetX(0)
			.withOffsetY(this.offsetY);

		for (let i = 1; i < this.positions.length; i++) {
			strategy.withFallbackPosition(
				{
					originX: this.positions[i].originX,
					originY: this.positions[i].originY,
				} as OriginConnectionPosition,
				{
					overlayX: this.positions[i].overlayX,
					overlayY: this.positions[i].overlayY,
				} as OverlayConnectionPosition
			);
		}

		return strategy;
	}

	private _emit(option: Option, shouldForwardEvent: boolean = true) {
		const newValue = option.value;
		const newLabel = option.text;

		this.triggerElement.getElementsByClassName(
			`${this.config.classes.component}__trigger__value-text`
		)[0].textContent = newLabel;
		this.triggerElement.setAttribute('data-value', newValue);

		this.selectElement.value = newValue;
		this.selectedIndex = this._getOptionIndex(option);
		this.activeIndex = this.selectedIndex;
		this.selectElement.selectedIndex = this.selectedIndex;

		if (shouldForwardEvent) {
			this._shouldForward('change');
		}
	}

	private _render(): string {
		const options = this.options.reduce((value, option, index) => {
			return `${value}<div class="${
				this.config.classes.component
			}__panel__option${
				option.disabled ? ` ${this.config.classes.disabled}` : ''
			}">
                    ${option.text}
                </div>`;
		}, '');

		const arrowHtml =
			this.config.classes.iconHtml !== undefined &&
			this.config.classes.iconHtml !== null &&
			this.config.classes.iconHtml.length > 0
				? this.config.classes.iconHtml
				: `<span class="${this.config.classes.component}__trigger__arrow"></span>`;

		const trigger = this.options
			.filter((option, index) => {
				return index === this.selectedIndex;
			})
			.reduce((value, option) => {
				return `${value}<div class="${this.config.classes.component}__trigger">
                        <span class="${this.config.classes.component}__trigger__value">
                            <span class="${this.config.classes.component}__trigger__value-text">
                                ${option.text}
                            </span>
                        </span>
                        ${arrowHtml}
                    </div>`;
			}, '');

		this.panelHTML = `<div class="${this.config.classes.component}__panel">${options}</div>`;

		return `<div class="${this.config.classes.component}" id="${this.uuid}">${trigger}</div>`;
	}

	/** Gets the index of the provided option in the option list. */
	private _getOptionIndex(option: Option): number | undefined {
		return this.options.reduce(
			(result: number, current: Option, index: number) => {
				return result === undefined
					? option === current
						? index
						: undefined
					: result;
			},
			undefined
		);
	}

	/** Scrolls the active option into view. */
	private _scrollActiveOptionIntoView(): void {
		const itemHeight = this._getItemHeight();
		const activeOptionIndex = this.activeIndex || 0;
		const scrollOffset = activeOptionIndex * itemHeight;
		const panelTop = this.overlayRef.portalElement.scrollTop;
		const maxHeight = this._getPanelMaxHeight();

		if (scrollOffset < panelTop) {
			this.overlayRef.portalElement.scrollTop = scrollOffset;
		} else if (scrollOffset + itemHeight > panelTop + maxHeight) {
			this.overlayRef.portalElement.scrollTop = Math.max(
				0,
				scrollOffset - maxHeight + itemHeight
			);
		}
	}

	/** Calculates the scroll position and x- and y-offsets of the overlay panel. */
	private _calculateOverlayPosition(): void {
		const itemHeight = this._getItemHeight();
		const items = this.options.length;
		const panelHeight = Math.min(items * itemHeight, this._getPanelMaxHeight());
		const scrollContainerHeight = items * itemHeight;

		// The farthest the panel can be scrolled before it hits the bottom
		const maxScroll = scrollContainerHeight - panelHeight;

		// If no value is selected we open the popup to the first item.
		const selectedOptionOffset = this.selectedIndex!;

		// We must maintain a scroll buffer so the selected option will be scrolled to the
		// center of the overlay panel rather than the top.
		const scrollBuffer = panelHeight / 2;
		this.scrollTop = this._calculateOverlayScroll(
			selectedOptionOffset,
			scrollBuffer,
			maxScroll
		);
		this.offsetY = this._calculateOverlayOffsetY(
			selectedOptionOffset,
			scrollBuffer,
			maxScroll
		);

		this._checkOverlayWithinViewport(maxScroll);
		this.position.withOffsetY(this.offsetY);
	}

	/**
	 * Checks that the attempted overlay position will fit within the viewport.
	 * If it will not fit, tries to adjust the scroll position and the associated
	 * y-offset so the panel can open fully on-screen. If it still won't fit,
	 * sets the offset back to 0 to allow the fallback position to take over.
	 */
	private _checkOverlayWithinViewport(maxScroll: number): void {
		const itemHeight = this._getItemHeight();
		const maxHeight = this._getPanelMaxHeight();
		const viewportSize = {
			width: window.innerWidth,
			height: window.innerHeight,
		};
		const paddingWidth = this._getPanelToViewportPadding();

		const topSpaceAvailable = this.triggerRect.top - paddingWidth;
		const bottomSpaceAvailable =
			viewportSize.height - this.triggerRect.bottom - paddingWidth;

		const panelHeightTop = Math.abs(this.offsetY);
		const totalPanelHeight = Math.min(
			this.options.length * itemHeight,
			maxHeight
		);
		const panelHeightBottom =
			totalPanelHeight - panelHeightTop - this.triggerRect.height;

		if (panelHeightBottom > bottomSpaceAvailable) {
			this._adjustPanelUp(panelHeightBottom, bottomSpaceAvailable);
		} else if (panelHeightTop > topSpaceAvailable) {
			this._adjustPanelDown(panelHeightTop, topSpaceAvailable, maxScroll);
		} else {
			this.overlayRef.portalElement.style.transformOrigin = this._getOriginBasedOnOption();
		}
	}

	/** Adjusts the overlay panel up to fit in the viewport. */
	private _adjustPanelUp(
		panelHeightBottom: number,
		bottomSpaceAvailable: number
	) {
		// Browsers ignore fractional scroll offsets, so we need to round.
		const distanceBelowViewport = Math.round(
			panelHeightBottom - bottomSpaceAvailable
		);

		// Scrolls the panel up by the distance it was extending past the boundary, then
		// adjusts the offset by that amount to move the panel up into the viewport.
		this.scrollTop -= distanceBelowViewport;
		this.offsetY -= distanceBelowViewport;
		this.overlayRef.portalElement.style.transformOrigin = this._getOriginBasedOnOption();

		// If the panel is scrolled to the very top, it won't be able to fit the panel
		// by scrolling, so set the offset to 0 to allow the fallback position to take
		// effect.
		if (this.triggerRect.top + this.scrollTop <= 0) {
			this.scrollTop = 0;
			this.offsetY = 0;
			this.overlayRef.portalElement.style.transformOrigin = '50% bottom 0px';
		}
	}

	/** Adjusts the overlay panel down to fit in the viewport. */
	private _adjustPanelDown(
		panelHeightTop: number,
		topSpaceAvailable: number,
		maxScroll: number
	) {
		// Browsers ignore fractional scroll offsets, so we need to round.
		const distanceAboveViewport = Math.round(
			panelHeightTop - topSpaceAvailable
		);

		// Scrolls the panel down by the distance it was extending past the boundary, then
		// adjusts the offset by that amount to move the panel down into the viewport.
		this.scrollTop += distanceAboveViewport;
		this.offsetY += distanceAboveViewport;
		this.overlayRef.portalElement.style.transformOrigin = this._getOriginBasedOnOption();

		// If the panel is scrolled to the very bottom, it won't be able to fit the
		// panel by scrolling, so set the offset to 0 to allow the fallback position
		// to take effect.
		if (this.triggerRect.top + this.scrollTop >= maxScroll) {
			this.scrollTop = maxScroll;
			this.offsetY = 0;
			this.overlayRef.portalElement.style.transformOrigin = '50% top 0px';
			return;
		}
	}

	/** Sets the transform origin point based on the selected option. */
	private _getOriginBasedOnOption(): string {
		const itemHeight = this._getItemHeight();
		const optionHeightAdjustment = (itemHeight - this.triggerRect.height) / 2;
		const originY =
			Math.abs(this.offsetY) - optionHeightAdjustment + itemHeight / 2;
		return `50% ${originY}px 0px`;
	}

	/**
	 * Calculates the scroll position of the select's overlay panel.
	 *
	 * Attempts to center the selected option in the panel. If the option is
	 * too high or too low in the panel to be scrolled to the center, it clamps the
	 * scroll position to the min or max scroll positions respectively.
	 */
	private _calculateOverlayScroll(
		selectedIndex: number,
		scrollBuffer: number,
		maxScroll: number
	): number {
		const itemHeight = this._getItemHeight();
		const optionOffsetFromScrollTop = itemHeight * selectedIndex;
		const halfOptionHeight = itemHeight / 2;

		// Starts at the optionOffsetFromScrollTop, which scrolls the option to the top of the
		// scroll container, then subtracts the scroll buffer to scroll the option down to
		// the center of the overlay panel. Half the option height must be re-added to the
		// scrollTop so the option is centered based on its middle, not its top edge.
		const optimalScrollPosition =
			optionOffsetFromScrollTop - scrollBuffer + halfOptionHeight;
		return Math.min(Math.max(0, optimalScrollPosition), maxScroll);
	}

	/**
	 * Sets the x-offset of the overlay panel in relation to the trigger's top start corner.
	 * This must be adjusted to align the selected option text over the trigger text when
	 * the panel opens. Will change based on LTR or RTL text direction. Note that the offset
	 * can't be calculated until the panel has been attached, because we need to know the
	 * content width in order to constrain the panel within the viewport.
	 */
	private _calculateOverlayOffsetX(): void {
		const overlayRect = this.overlayRef.overlayElement.getBoundingClientRect();
		const viewportSize = {
			width: window.innerWidth,
			height: window.innerHeight,
		};
		const paddingWidth = this._getPanelToViewportPadding();
		let offsetX = 0;

		// todo: multiple
		// Adjust the offset, depending on the option padding.
		// offsetX = this.selectedIndex ? padding.panel * 2 : padding.panel;

		// Invert the offset in LTR.
		offsetX *= -1;

		// Determine how much the select overflows on each side.
		const leftOverflow = 0 - (overlayRect.left + offsetX);
		const rightOverflow =
			overlayRect.right +
			offsetX -
			viewportSize.width; /* +
            padding.panel * 2*/

		// If the element overflows on either side, reduce the offset to allow it to fit.
		if (leftOverflow > 0) {
			offsetX += leftOverflow + paddingWidth;
		} else if (rightOverflow > 0) {
			offsetX -= rightOverflow + paddingWidth;
		}

		// Set the offset directly
		this.position.withOffsetX(offsetX);
		this.overlayRef.updatePosition();
	}

	/**
	 * Calculates the y-offset of the select's overlay panel in relation to the
	 * top start corner of the trigger. It has to be adjusted in order for the
	 * selected option to be aligned over the trigger when the panel opens.
	 */
	private _calculateOverlayOffsetY(
		selectedIndex: number,
		scrollBuffer: number,
		maxScroll: number
	): number {
		const itemHeight = this._getItemHeight();
		const optionHeightAdjustment = (itemHeight - this.triggerRect.height) / 2;
		const maxHeight = this._getPanelMaxHeight();
		const maxOptionsDisplayed = Math.floor(maxHeight / itemHeight);
		const items = this.options.length;
		let optionOffsetFromPanelTop: number;

		if (this.scrollTop === 0) {
			optionOffsetFromPanelTop = selectedIndex * itemHeight;
		} else if (this.scrollTop === maxScroll) {
			const firstDisplayedIndex = items - maxOptionsDisplayed;
			const selectedDisplayIndex = selectedIndex - firstDisplayedIndex;

			// The first item is partially out of the viewport. Therefore we need to calculate what
			// portion of it is shown in the viewport and account for it in our offset.
			const partialItemHeight =
				itemHeight - ((items * itemHeight - maxHeight) % itemHeight);

			// Because the panel height is longer than the height of the options alone,
			// there is always extra padding at the top or bottom of the panel. When
			// scrolled to the very bottom, this padding is at the top of the panel and
			// must be added to the offset.
			optionOffsetFromPanelTop =
				selectedDisplayIndex * itemHeight + partialItemHeight;
		} else {
			// If the option was scrolled to the middle of the panel using a scroll buffer,
			// its offset will be the scroll buffer minus the half height that was added to
			// center it.
			optionOffsetFromPanelTop = scrollBuffer - itemHeight / 2;
		}

		// The final offset is the option's offset from the top, adjusted for the height
		// difference, multiplied by -1 to ensure that the overlay moves in the correct
		// direction up the page.
		return optionOffsetFromPanelTop * -1 - optionHeightAdjustment;
	}

	private _highlightCorrectOption() {
		this.optionElements.forEach((option) => {
			removeClass(option, this.config.classes.active);
			removeClass(option, this.config.classes.highlighted);
		});

		addClass(
			this.optionElements[this.selectedIndex],
			this.config.classes.active
		);

		if (this.selectedIndex !== this.activeIndex) {
			addClass(
				this.optionElements[this.activeIndex],
				this.config.classes.highlighted
			);
		}
	}

	private _getPanelToViewportPadding() {
		const option = this.optionElements[0]!;

		// todo: cache?
		const padding = parseCssUnit(getCss(option, 'padding-left'));

		return Math.round(padding / 2);
	}

	private _getPanelMaxHeight(): number {
		// todo: cache?
		return parseCssUnit(getCss(this.overlayRef.portalElement!, 'max-height'));
	}

	private _getItemHeight(): number {
		return this.optionElements[0]!.offsetHeight!;
	}

	/**
	 * When the panel element is finished transforming in (though not fading in), it
	 * emits an event and focuses an option if the panel is open.
	 */
	private _onPanelDone(): void {
		if (this.panelOpen) {
			this.scrollTop = 0;
		} else {
			this.position.withOffsetX(0);
		}
	}

	/**
	 * Forward events to the <select/> element
	 */
	private _shouldForward(eventName: string) {
		const event = document.createEvent('HTMLEvents');
		event.initEvent(eventName, false, true);
		this.selectElement.dispatchEvent(event);
	}

	private _getSelectedIndex() {
		if (this.selectElement.selectedIndex >= 0) {
			return this.selectElement.selectedIndex;
		}

		// TODO: Investigate why this.selectElement.selectedIndex is sometimes -1
		// even we have selected="selected"! Occurs when you navigate back!
		const selected = this.selectElement.querySelector<HTMLOptionElement>(
			'option[selected]'
		);

		if (!selected) {
			return 0;
		}

		return Array.from(selected.parentElement.children).indexOf(selected);
	}
}

export function select(element: HTMLSelectElement): Select {
	return new Select(element);
}
